import React, { useState } from 'react';
import { SectionHead, Icon, Action, CheckList } from './Primitives';
import { catalog, money, number, gross, quote } from '../config/pricing';
import './UnityPricing.css';
const descriptions = {
  create: 'For independent creators building training materials.',
  'create-teams': 'For teams building and refining learning together.',
  deliver: 'For organisations ready to run training in Unity.',
  impact: 'For delivery with funder and supervisor oversight.',
};
const features = {
  create: [
    'Manual and AI-assisted creation',
    'Rich lessons and author preview',
    'SCORM 1.2 study exports + PDFs',
  ],
  'create-teams': [
    'Everything in Create',
    'Shared authoring workspace',
    'Add creators as your team grows',
  ],
  deliver: [
    'Create and publish programmes',
    'Learner + facilitator workspaces',
    'Adaptive complexity + translations',
    'Assessments, badges + reports',
  ],
  impact: [
    'Everything in Deliver',
    'Funder + supervisor reporting',
    'Scoped stakeholder access',
    'Contextual learning expeditions',
  ],
};
export default function UnityPricing({ onTrial, onDemo, vatRate = 0.15, onEvent }) {
  const [annual, setAnnual] = useState(false);
  const [planId, setPlan] = useState('deliver');
  const [learners, setLearners] = useState(100);
  const [creators, setCreators] = useState(3);
  const [facilitators, setFacilitators] = useState(3);
  const quoteResult = quote({ planId, annual, learners, creators, facilitators, vatRate });
  const changePlan = (id) => {
    setPlan(id);
    const p = catalog.plans.find((p) => p.id === id);
    setCreators(p.includedCreators);
    setFacilitators(p.includedFacilitators);
    setLearners(p.includedMonthlyActiveLearners);
  };
  const inputValue = (e) => Math.min(10000, Math.max(0, Math.floor(Number(e.target.value) || 0)));
  return (
    <section className="ul-section ul-pricing" id="pricing">
      <div className="ul-container">
        <SectionHead
          center
          eyebrow="Choose your starting point"
          title={
            <>
              Start with what you need.
              <br />
              Grow into what’s next.
            </>
          }
        >
          Explore proposed plans with monthly AI allowances. Create training for your existing
          LMS, or bring the whole learning journey into Unity.
        </SectionHead>
        <p className="ul-pricing-trial-note">
          Indicative plans and pricing. Our team confirms availability, included allowances and
          final terms before you start.
        </p>
        <div className="ul-billing-choice">
          <div className="ul-segmented" aria-label="Subscription billing frequency">
            <button
              type="button"
              aria-pressed={!annual}
              onClick={() => {
                setAnnual(false);
                onEvent?.('billing_cycle_selected', { cycle: 'monthly' });
              }}
            >
              Monthly
            </button>
            <button
              type="button"
              aria-pressed={annual}
              onClick={() => {
                setAnnual(true);
                onEvent?.('billing_cycle_selected', { cycle: 'annual' });
              }}
            >
              Annually <span>Save 15%</span>
            </button>
          </div>
          <p>Annual saving applies to the platform subscription.</p>
        </div>
        <div className="ul-plan-groups">
          <span>Create training</span>
          <span>Run training</span>
        </div>
        <div className="ul-plan-grid">
          {catalog.plans.map((plan) => {
            const cents = Math.round(
              (annual ? plan.annualMonthlyEquivalentZar : plan.monthlyBaseZar) * 100,
            );
            return (
              <article
                key={plan.id}
                className={`ul-plan ${plan.id === 'deliver' ? 'ul-plan--recommended' : ''}`}
              >
                <div className="ul-plan-badge">
                  {plan.id === 'deliver'
                    ? 'RECOMMENDED FOR DELIVERY'
                    : plan.id === 'impact'
                      ? 'PORTFOLIO OVERSIGHT'
                      : ' '}
                </div>
                <h3>{plan.name}</h3>
                <p className="ul-plan-description">{descriptions[plan.id]}</p>
                <div className="ul-plan-price">
                  <b>{money(cents)}</b>
                  <span>ex VAT / month{annual ? ' equivalent' : ''}</span>
                  {vatRate > 0 && (
                    <>
                      <b>{money(gross(cents, vatRate))}</b>
                      <span>incl VAT / month{annual ? ' equivalent' : ''}</span>
                    </>
                  )}
                </div>
                {annual ? (
                  <p className="ul-plan-term">
                    {money(gross(Math.round(plan.annualBaseZar * 100), vatRate))}{' '}
                    {vatRate ? 'incl VAT ' : ''}billed annually. Usage and extra seats billed
                    separately.
                  </p>
                ) : (
                  <p className="ul-plan-term">
                    Billed monthly. Usage and extra seats are additional where applicable.
                  </p>
                )}
                <div className="ul-plan-ai">
                  <Icon name="spark" size={17} />
                  <span>
                    <strong>{number(plan.monthlyAiCredits)}</strong> AI credits / month
                  </span>
                </div>
                <ul className="ul-plan-capacity">
                  <li>
                    <b>{plan.includedCreators}</b>{' '}
                    {plan.includedCreators === 1 ? 'creator' : 'creators'}
                  </li>
                  {plan.includedFacilitators > 0 && (
                    <li>
                      <b>{plan.includedFacilitators}</b> additional facilitator-only seats
                    </li>
                  )}
                  <li>
                    {plan.includedMonthlyActiveLearners ? (
                      <>
                        <b>{plan.includedMonthlyActiveLearners}</b> monthly active learners
                      </>
                    ) : (
                      <>Authoring and export · no learner delivery</>
                    )}
                  </li>
                </ul>
                <CheckList items={features[plan.id]} />
                <Action secondary={plan.id !== 'deliver'} onClick={() => onTrial(plan.id)}>
                  Enquire about this plan
                </Action>
                {plan.id === 'impact' && (
                  <button type="button" className="ul-plan-demo" onClick={onDemo}>
                    Talk to us about your rollout
                  </button>
                )}
              </article>
            );
          })}
        </div>
        <p className="ul-pricing-trial-note">
          Ask about an authoring trial for one creator, one programme structure and one outcome
          of content. Our team confirms trial access and limits. Discuss live learner delivery
          through a Deliver or Impact plan.
        </p>
        <details className="ul-plan-compare">
          <summary>
            Compare the plan essentials <Icon name="plus" size={18} />
          </summary>
          <div className="ul-table-scroll">
            <table>
              <caption className="ul-sr-only">Key feature comparison for Unity plans</caption>
              <thead>
                <tr>
                  <th scope="col">Feature</th>
                  {catalog.plans.map((p) => (
                    <th key={p.id} scope="col">
                      {p.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ['Shared authoring', false, true, true, true],
                  ['Native learner delivery', false, false, true, true],
                  ['Adaptive complexity', false, false, true, true],
                  ['Native language editions', false, false, true, true],
                  ['Operational reports and badges', false, false, true, true],
                  ['Funder / supervisor reports', false, false, false, true],
                  ['Learning expeditions', false, false, false, true],
                ].map(([feature, ...values]) => (
                  <tr key={feature}>
                    <th scope="row">{feature}</th>
                    {values.map((v, i) => (
                      <td key={i}>{v ? 'Included' : '—'}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
        <div className="ul-cost-calculator">
          <div>
            <p className="ul-eyebrow">See the cost as you grow</p>
            <h3>
              Your team. Your learner count.
              <br />A clearer monthly estimate.
            </h3>
            <p className="ul-note">
              This indicative estimate uses the proposed usage bands: one learner counts once per
              month across programmes. Extra active learners are R35 each through 500 total, then
              R25 each through 2,000, excluding VAT. Your final quote confirms pricing and tax.
            </p>
            <div className="ul-calculator-fields">
              <label>
                Plan
                <select value={planId} onChange={(e) => changePlan(e.target.value)}>
                  <option value="deliver">Deliver</option>
                  <option value="impact">Impact</option>
                </select>
              </label>
              <label>
                Monthly active learners
                <input
                  type="number"
                  min="0"
                  max="10000"
                  step="1"
                  value={learners}
                  onChange={(e) => setLearners(inputValue(e))}
                />
              </label>
              <label>
                Creators
                <input
                  type="number"
                  min="0"
                  max="10000"
                  value={creators}
                  onChange={(e) => setCreators(inputValue(e))}
                />
              </label>
              <label>
                Facilitator-only staff
                <input
                  type="number"
                  min="0"
                  max="10000"
                  value={facilitators}
                  onChange={(e) => setFacilitators(inputValue(e))}
                />
              </label>
            </div>
          </div>
          <div className="ul-calculator-result" aria-live="polite" aria-atomic="true">
            {quoteResult.requiresQuote ? (
              <>
                <span>MORE THAN 2,000 ACTIVE LEARNERS</span>
                <h4>Let’s plan your rollout.</h4>
                <p>We’ll scope capacity, support and commercial terms together.</p>
                <Action onClick={onDemo}>Request a quote</Action>
              </>
            ) : (
              <>
                <span>{annual ? 'INDICATIVE MONTHLY EQUIVALENT' : 'INDICATIVE MONTHLY COST'}</span>
                <strong>{money(quoteResult.grossCents)}</strong>
                <p>
                  {vatRate ? 'Including VAT' : 'No VAT applied'} · {money(quoteResult.netCents)}{' '}
                  excluding VAT
                </p>
                <dl>
                  <div>
                    <dt>Platform</dt>
                    <dd>{money(quoteResult.baseCents)}</dd>
                  </div>
                  <div>
                    <dt>Additional learners</dt>
                    <dd>{money(quoteResult.learnerCents)}</dd>
                  </div>
                  <div>
                    <dt>Additional staff</dt>
                    <dd>{money(quoteResult.creatorCents + quoteResult.facilitatorCents)}</dd>
                  </div>
                </dl>
                <small>
                  Estimate only. Breakdown excludes VAT and optional AI top-ups.{' '}
                  {annual
                    ? `${money(gross(quoteResult.annualBaseCents, vatRate))} platform fee paid annually; extra usage and seats billed monthly.`
                    : 'Proposed included capacity is applied to this estimate.'}
                </small>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
